// Whose Opportunities list preferences to read and write (spec 010 D9, QA D-8).
// Every preference module derives its storage key from the scope string, so qualifying the host's scope with the
// signed-in person gives each person on a shared browser their own column visibility, layout, custom field
// columns, tab and sort (US2 AC6), still in the browser's own storage (FR2.3) and without touching those modules.

/** The bare host scope when there is no person, so a list rendered without a viewer behaves as before. */
export const personPreferenceScope = (scope: string, personId: string | null | undefined): string =>
	personId ? `${scope}-person-${personId}` : scope;

// The localStorage keys the preference modules write for a scope. Spelled out rather than imported: they name
// what was saved under the bare scope before D-8, which must stay fixed even if a module's live key changes.
const localKeys = (scope: string): string[] => [
	`columns-${scope}-v1`,
	`column-layout-${scope}-v1`,
	`custom-field-columns-${scope}-v1`,
];

/**
 * The person's scope, having handed them what was saved under the bare scope before preferences were per person.
 * The first person to open the list afterwards takes it over, so nobody's existing layout is lost, and it is then
 * removed, so the next person on the same browser starts from the defaults rather than someone else's. A person
 * who already has their own entry keeps it. Only localStorage moves: the tab and sort live in sessionStorage,
 * which ends with the browser tab.
 */
export function claimPersonPreferences(scope: string, personId: string | null | undefined): string {
	const personal = personPreferenceScope(scope, personId);
	if (personal === scope) return scope;
	try {
		const personalKeys = localKeys(personal);
		localKeys(scope).forEach((key, i) => {
			const value = localStorage.getItem(key);
			if (value === null) return;
			if (localStorage.getItem(personalKeys[i]) === null) localStorage.setItem(personalKeys[i], value);
			localStorage.removeItem(key);
		});
	} catch {
		/* localStorage unavailable */
	}
	return personal;
}
